/**
 * Resolve post-login path from the tenant's configured home destination.
 * Falls back to /dashboard (hybrid launchpad). VITE_LANDING_STUDIO=1 → /studio.
 */

export const DEFAULT_TENANT_HOME = '/dashboard';

function studioFirstOverride() {
  return import.meta.env.VITE_LANDING_STUDIO === '1' ? '/studio' : null;
}

/**
 * @param {{ fetchImpl?: typeof fetch, token?: string|null }} [opts]
 * @returns {Promise<string>}
 */
export async function fetchTenantHomePath(opts = {}) {
  const override = studioFirstOverride();
  if (override) return override;

  const fetchImpl = opts.fetchImpl || (typeof fetch === 'function' ? fetch.bind(globalThis) : null);
  if (!fetchImpl) return DEFAULT_TENANT_HOME;

  const token = opts.token !== undefined ? opts.token : localStorage.getItem('tkn');
  const headers = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  try {
    const res = await fetchImpl('/api/v1/tenants/me/home-destination', {
      credentials: 'include',
      headers,
    });
    if (!res.ok) return DEFAULT_TENANT_HOME;
    const data = await res.json();
    const path = typeof data?.path === 'string' ? data.path.trim() : '';
    if (path.startsWith('/') && !path.startsWith('//')) return path;
    return DEFAULT_TENANT_HOME;
  } catch {
    return DEFAULT_TENANT_HOME;
  }
}

/**
 * Persist home destination for a tenant (admin / super_admin).
 * @param {string} tenantId
 * @param {{ type: string, id?: string|null }} body
 */
export async function putTenantHomeDestination(tenantId, body) {
  const token = localStorage.getItem('tkn');
  const res = await fetch(`/api/v1/tenants/${encodeURIComponent(tenantId)}/home-destination`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message || 'Failed to save home destination');
  }
  return data;
}
